import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import pino from 'pino';
import { Subject, timer } from 'rxjs';
import {
    bufferTime,
    delayWhen,
    retryWhen,
    scan,
    switchMap,
    takeWhile,
} from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { TakeUntilDestroy } from '../common/take-until-destroy.decorator';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class LoggerService implements OnDestroy {
    logger: pino.Logger;

    fatal: pino.LogFn;
    error: pino.LogFn;
    warn: pino.LogFn;
    info: pino.LogFn;
    debug: pino.LogFn;
    trace: pino.LogFn;
    silent: pino.LogFn;

    /* move??? */
    cpeId: string;

    private readonly _logsSubj: Subject<pino.LogEvent>;

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this.cpeId = String(Math.random());

        this._logsSubj = new Subject();
        this._logsSubj
            .asObservable()
            .pipe(
                bufferTime(10000, null, 100),
                switchMap((logEvents) => {
                    const body = {
                        cpeId: this.cpeId,
                        events: logEvents.map((e) => JSON.stringify(e)),
                    };
                    return this._http
                        .post(
                            `${this._appConfig.API_ENDPOINTS.UI_AGENT}/logs`,
                            body,
                        )
                        .pipe(
                            /* retry 5 times or until the next log events (switchMap) are emitted */
                            retryWhen((errors) =>
                                errors.pipe(
                                    delayWhen(() => timer(1000)),
                                    scan((acc) => {
                                        return acc + 1;
                                    }, 0),
                                    takeWhile((acc) => acc < 5),
                                ),
                            ),
                        );
                }),
            )
            .subscribe();

        this.logger = pino({
            level: 'debug',
            browser: {
                asObject: true,
                transmit: {
                    level: 'debug',
                    send: (_, logEvent): void => {
                        this._logsSubj.next(logEvent);
                    },
                },
            },
        });

        const levels = [
            'fatal',
            'error',
            'warn',
            'info',
            'debug',
            'trace',
            'silent',
        ];

        levels.forEach((level) => {
            this[level] = (...args: any[]): void => {
                this.logger[level](...args);
            };
        });
    }

    ngOnDestroy(): void {
        this._logsSubj.complete();
    }
}
